/**
 * Estimates, landing page: what's in progress, a way to start a new one, and
 * the quick-compare tool for when there's nothing to save yet — just a
 * question about what a job would cost. See docs/estimating.md.
 */
import { useState } from "react";
import { AssistantHint } from "../AssistantHint";
import { api, unwrap } from "../api";
import { Button, Empty, ErrorNote, Field, Loading, Panel, Table, Td, Th, input } from "../ui";
import { useResource } from "../useResource";
import { StatusPill } from "./Contracts";
import { QuickCompare } from "./estimating";
import { ClientPicker } from "./shared";

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  issued: "Issued",
  won: "Won",
  lost: "Lost",
  void: "Void",
};

export function Estimates({
  slug,
  currency,
  role,
  onOpenEstimate,
  onOpenContract,
}: {
  slug: string;
  currency: string;
  role: string;
  onOpenEstimate: (id: string) => void;
  onOpenContract?: (id: string) => void;
}) {
  const canCreate = role === "manager" || role === "admin" || role === "owner";
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [clientId, setClientId] = useState("");

  const r = useResource({
    key: ["estimates", slug],
    list: async () => unwrap(await api.api.c[":slug"].estimates.$get({ param: { slug } })),
    create: async (body: { name: string; currency: string; clientId?: string | null }) =>
      unwrap(await api.api.c[":slug"].estimates.$post({ param: { slug }, json: body })),
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const created = await r.create({
        name: name.trim(),
        currency,
        // Optional here on purpose: an estimate often starts as "what would
        // this cost", before anyone knows whose job it is. Naming the client
        // now is what lets a proposal be built from it in one step later.
        clientId: clientId || null,
      });
      setName("");
      setClientId("");
      setOpen(false);
      onOpenEstimate((created as { estimate: { id: string } }).estimate.id);
    } catch {
      /* surfaced through r.formError */
    }
  }

  const estimates = r.query.data?.estimates ?? [];

  return (
    <div className="flex flex-col gap-6">
      <AssistantHint what="Price a job out loud, from measurements." />
      <Panel
        title="Estimates"
        action={
          canCreate && (
            <Button
              onClick={() => {
                setOpen(!open);
                r.clearErrors();
              }}
            >
              {open ? "Cancel" : "New estimate"}
            </Button>
          )
        }
      >
        {open && (
          <form
            onSubmit={submit}
            className="mb-6 flex items-end gap-3 rounded-xl border border-neutral-200 bg-white p-5 shadow-sm"
          >
            {r.formError && <ErrorNote>{r.formError}</ErrorNote>}
            <div className="flex-1">
              <Field label="Name" error={r.fieldErrors.name}>
                <input
                  className={input}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Miller kitchen"
                  required
                />
              </Field>
            </div>
            <div className="flex-1">
              <ClientPicker
                slug={slug}
                value={clientId}
                onChange={setClientId}
                hint="Optional — needed to turn this into a proposal"
                error={r.fieldErrors.clientId}
              />
            </div>
            <Button type="submit" disabled={r.saving}>
              {r.saving ? "Creating…" : "Create"}
            </Button>
          </form>
        )}

        {r.query.isPending ? (
          <Loading />
        ) : r.query.isError ? (
          <ErrorNote>{(r.query.error as Error).message}</ErrorNote>
        ) : estimates.length === 0 ? (
          <Empty title="No estimates yet">
            Start one to compare contractor bids and price a job to the client.
          </Empty>
        ) : (
          <Table
            head={
              <>
                <Th>Name</Th>
                <Th>Client</Th>
                <Th>Status</Th>
                <Th>Proposal</Th>
                <Th>Priced as of</Th>
                <Th />
              </>
            }
          >
            {estimates.map((e) => (
              <tr
                key={e.id}
                tabIndex={0}
                className="cursor-pointer border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100 focus:bg-neutral-100 focus:outline-none"
                onClick={() => onOpenEstimate(e.id)}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter") onOpenEstimate(e.id);
                }}
              >
                <Td>{e.name}</Td>
                <Td muted>{e.clientName ?? "—"}</Td>
                <Td muted>{STATUS_LABELS[e.status] ?? e.status}</Td>
                <Td>
                  {e.proposalId ? (
                    onOpenContract ? (
                      <button
                        type="button"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          onOpenContract(e.proposalId!);
                        }}
                        className="inline-flex items-center gap-1.5 hover:underline"
                      >
                        <span className="font-mono text-xs text-neutral-500">
                          {e.proposalNumber}
                        </span>
                        <StatusPill status={e.proposalStatus ?? "draft"} />
                      </button>
                    ) : (
                      <StatusPill status={e.proposalStatus ?? "draft"} />
                    )
                  ) : (
                    <span className="text-neutral-300">—</span>
                  )}
                </Td>
                <Td mono muted>
                  {e.pricedOn}
                </Td>
                <Td right>
                  <Button variant="ghost" onClick={() => onOpenEstimate(e.id)}>
                    Open
                  </Button>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      <QuickCompare slug={slug} currency={currency} />
    </div>
  );
}
